import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AiIcon } from '../../AiIcon';
import { MenuContainer, MenuGroup, MenuItem } from './styles';

const MENU_WIDTH = 208;
const MENU_OFFSET = 4;

export interface HistoryContextMenuProps {
  anchor: HTMLElement;
  onRename: () => void;
  onDelete: () => void;
  onClose: () => void;
}

/** "Chat AI / Sidebar / History / Item / Context Menu" do Figma. */
export const HistoryContextMenu: FC<HistoryContextMenuProps> = ({
  anchor,
  onRename,
  onDelete,
  onClose
}) => {
  const { t } = useTranslation('activities');
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    const rect = anchor.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight ?? 0;
    const fitsBelow = rect.bottom + MENU_OFFSET + menuHeight <= window.innerHeight;
    setPosition({
      top: fitsBelow ? rect.bottom + MENU_OFFSET : rect.top - MENU_OFFSET - menuHeight,
      left: Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8)
    });
  }, [anchor]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target) && !anchor.contains(target)) {
        onClose();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [anchor, onClose]);

  return createPortal(
    <MenuContainer ref={menuRef} role="menu" style={position}>
      <MenuGroup>
        <MenuItem type="button" role="menuitem" onClick={onRename}>
          <AiIcon name="menuEdit" />
          <span>{t('aiChat.rename')}</span>
        </MenuItem>
      </MenuGroup>
      <MenuGroup>
        <MenuItem type="button" role="menuitem" onClick={onDelete}>
          <AiIcon name="menuTrash" />
          <span>{t('aiChat.deleteChat')}</span>
        </MenuItem>
      </MenuGroup>
    </MenuContainer>,
    document.body
  );
};
